import React from 'react';
import { Auth } from '@capillarytech/cap-ui-utils';
import { v4 as uuidv4 } from 'uuid';
import { FormattedHTMLMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import sum from 'lodash/sum';
import values from 'lodash/values';

import {
  AUDIENCE_CAMPAIGNS,
  BADGES_UI_ENABLED,
  BADGE_FIXED_VALUE,
  BADGE_ROLLING_WINDOW,
  CALANDER_MONTH,
  CART_PROMOTION,
  COUPON,
  CUSTOMER_BADGE_FIXED_VALUE,
  CUSTOMER_BADGE_ROLLING_WINDOW,
  ENABLE_BADGE_BENEFITS,
  GIFT_CARDS,
  GIFT_VOUCHER,
  INDIVIDUAL_CUSTOMER_LIMIT,
  JOURNEYS,
  LOYALTY,
  LOYALTY_POINTS,
  PROMO_ENGINE,
  SPECIFIC_NO_OF_DAYS,
  SPECIFIC_WEEKDAY,
  errorMessageMap,
  supportedBadgeBenefits,
} from '../components/pages/App/constants';
import globalMessages from '../components/pages/Cap/messages';
import {
  DAYS,
  FRIDAY,
  MAX_IMG_SIZE_IN_MB,
  MONDAY,
  MONTHS,
  SATURDAY,
  SUNDAY,
  THURSDAY,
  TUESDAY,
  WEDNESDAY,
  WEEKS,
} from '../components/pages/Cap/constants';
import { LOYALTY_PROMOTION, moduleFilterMap } from '../components/organisms/BadgesList/constants';

export const parseQueryParams = (url = '') => {
  const queryString = url.split('?')[1] || '';
  const queryParams = queryString.split('&') || [];
  const queryParamsObject = {};
  queryParams.forEach(queryParam => {
    const keyValue = queryParam.split('=') || [];
    const key = keyValue[0];
    const value = keyValue[1];
    queryParamsObject[key] = value;
  });

  return queryParamsObject;
};

export const hasBadgesUIFeature = Auth.hasFeatureAccess.bind(
  null,
  BADGES_UI_ENABLED,
);

export const isBenefitsEnabled = Auth.hasFeatureAccess.bind(
  null,
  ENABLE_BADGE_BENEFITS,
);

export const isCartPromotionBenefitEnabled = Auth.hasFeatureAccess.bind(
  null,
  PROMO_ENGINE,
);

export const isGiftVoucherBenefitEnabled = Auth.hasFeatureAccess.bind(
  null,
  GIFT_CARDS,
);

export const getErrorMessage = (error = '', args = '', formatMessage) => {
  const finalArgs =
    error === 'NOT_FOUND'
      ? {
          ...args,
          key: args.key
            .split('_')
            .map(word => (word === 'ID' ? word : word.toLowerCase()))
            .join(' '),
        }
      : args;
  return errorMessageMap[error]
    ? formatMessage(globalMessages[errorMessageMap[error]], finalArgs)
    : formatMessage(globalMessages.unableToFetchDetails);
};
export const getOwnerDetailsStrings = (
  formatMessage,
  ownerDetails,
  errorComponent,
) => {
  let finalData = {};
  Object.keys(ownerDetails).forEach(owner => {
    const claimData = ownerDetails[owner];

    finalData[owner] = claimData.map(details => {
      try {
        if (details?.error) {
          if (
            ['MODULE_NOT_IMPLEMENTED', 'ACCESS_FORBIDDEN'].includes(
              details?.error,
            )
          )
            return formatMessage(
              globalMessages[errorMessageMap[(details?.error)]],
              details?.args,
            );
          return errorComponent(
            getErrorMessage(details?.error, details?.args, formatMessage),
          );
        }
        switch (owner.toUpperCase()) {
          case LOYALTY:
          case LOYALTY_PROMOTION:  {
            const {
              program = '',
              activity = '',
              set = 'NULL',
              id = 'NULL'
            } = details;
            return formatMessage(globalMessages.OwnerDetailTextLoyalty, {
              program,
              activity,
            });
          }
          case JOURNEYS: {
            const { journeyName = '' } = details;
            return formatMessage(globalMessages.OwnerDetailTextJourney, {
              journeyName
            });
          }
          case AUDIENCE_CAMPAIGNS: {
            const { campaignName = '' } = details;
            return formatMessage(globalMessages.OwnerDetailTextCampaign, {
              campaignName
            });
          }
          default: {
            return '-';
          }
        }
      } catch (error) {
        return errorComponent(
          getErrorMessage(undefined, undefined, formatMessage),
        );
      }
    });
  });
  return finalData;
};

export const getUniqueId = () => uuidv4();

export const getInstructions = [
  <>
    <FormattedHTMLMessage {...globalMessages.uploadInstruction1} />
  </>,
  <>
    <FormattedHTMLMessage
      {...globalMessages.uploadInstruction2}
      values={{ size: MAX_IMG_SIZE_IN_MB }}
    />
  </>,
  <>
    <FormattedHTMLMessage {...globalMessages.uploadInstruction3} />
  </>,
  <>
    <FormattedHTMLMessage {...globalMessages.uploadInstruction4} />
  </>,
];

export const isPositiveInteger = (value, includeZero = false) => {
  if(includeZero){
    return /^\d+$/.test(value) && parseInt(value) >= 0;
  }
  return /^\d+$/.test(value) && parseInt(value) > 0;
}


export const getEditBadgeExpiryPeriodOptions = (
  formatMessage,
  // isBadgeLive = false,
  // expiryPeriod = DAYS,
) => [
  {
    key: DAYS,
    value: DAYS,
    label: formatMessage(globalMessages.days),
    // disabled:
    //   isBadgeLive && (expiryPeriod === WEEKS || expiryPeriod === MONTHS),
  },
  {
    key: WEEKS,
    value: WEEKS,
    label: formatMessage(globalMessages.weeks),
    // disabled: isBadgeLive && expiryPeriod === MONTHS,
  },
  {
    key: MONTHS,
    value: MONTHS,
    label: formatMessage(globalMessages.months),
  },
];

export const getBadgeExpiryPeriodOptions_2 = formatMessage => [
  {
    key: SPECIFIC_NO_OF_DAYS,
    value: SPECIFIC_NO_OF_DAYS,
    label: formatMessage(globalMessages.specificNoOfDays),
  },
  {
    key: SPECIFIC_WEEKDAY,
    value: SPECIFIC_WEEKDAY,
    label: formatMessage(globalMessages.week),
  },
  {
    key: CALANDER_MONTH,
    value: CALANDER_MONTH,
    label: formatMessage(globalMessages.calendarMonth),
  },
];

export const getWeekDaysOptions = formatMessage => [
  {
    key: MONDAY,
    value: MONDAY,
    label: formatMessage(globalMessages.monday),
  },
  {
    key: TUESDAY,
    value: TUESDAY,
    label: formatMessage(globalMessages.tuesday),
  },
  {
    key: WEDNESDAY,
    value: WEDNESDAY,
    label: formatMessage(globalMessages.wednesday),
  },
  {
    key: THURSDAY,
    value: THURSDAY,
    label: formatMessage(globalMessages.thursday),
  },
  {
    key: FRIDAY,
    value: FRIDAY,
    label: formatMessage(globalMessages.friday),
  },
  {
    key: SATURDAY,
    value: SATURDAY,
    label: formatMessage(globalMessages.saturday),
  },
  {
    key: SUNDAY,
    value: SUNDAY,
    label: formatMessage(globalMessages.sunday),
  },
];

export const getBadgeExpiryDurationOptions = (
  formatMessage,
  limitType,
  isSpecificBadgeDurationMonthsDisabled = false,
  isOverAllBadgeDurationDisabled = false,
) => [
  {
    key:
      limitType === INDIVIDUAL_CUSTOMER_LIMIT
        ? CUSTOMER_BADGE_ROLLING_WINDOW
        : BADGE_ROLLING_WINDOW,
    value:
      limitType === INDIVIDUAL_CUSTOMER_LIMIT
        ? CUSTOMER_BADGE_ROLLING_WINDOW
        : BADGE_ROLLING_WINDOW,
    label: formatMessage(globalMessages.specificDuration),
    disabled: isSpecificBadgeDurationMonthsDisabled,
  },
  {
    key:
      limitType === INDIVIDUAL_CUSTOMER_LIMIT
        ? CUSTOMER_BADGE_FIXED_VALUE
        : BADGE_FIXED_VALUE,
    value:
      limitType === INDIVIDUAL_CUSTOMER_LIMIT
        ? CUSTOMER_BADGE_FIXED_VALUE
        : BADGE_FIXED_VALUE,
    label: formatMessage(globalMessages.overallBadgeDuration),
    disabled: isOverAllBadgeDurationDisabled,
  },
];

export const getBenefitsIconAndText = (iconType, formatMessage) => {
  switch (iconType) {
    case supportedBadgeBenefits[COUPON]:
      return {
        iconType: 'offer',
        displayText: formatMessage(globalMessages.issueCoupon),
      };
    case supportedBadgeBenefits[LOYALTY_POINTS]:
      return {
        iconType: 'points-v1',
        displayText: formatMessage(globalMessages.allocatePoints),
      };
    case supportedBadgeBenefits[CART_PROMOTION]:              
      return {
        iconType: 'cart-promotion',
        displayText: formatMessage(globalMessages.issueCartPromotion),
      };
    case supportedBadgeBenefits[GIFT_VOUCHER]:            
      return {
        iconType: 'gift-voucher',
        displayText: formatMessage(globalMessages.issueGiftVoucher),
      };
    default:
      return;
  }
};

export const getBadgeBenefitsCount = (
  pointsBenefits,
  couponsBenefits,
  cartPromotionsBenefits,
  giftVouchersBenefits,
) => {
  const benefitsCount = {
    COUPON: couponsBenefits?.length ?? 0,
    LOYALTY_POINTS: !isEmpty(pointsBenefits) ? 1 : 0,
    GIFT_VOUCHER: giftVouchersBenefits?.length ?? 0,
    CART_PROMOTION: cartPromotionsBenefits?.length ?? 0,
  };

  const totalBenefitsCount = sum(values(benefitsCount));

  return totalBenefitsCount;
};

export const getChangedKeys = (originalObj = {}, updatedObj = {}) => {
  const changedKeys = [];

  for (const key in originalObj) {
    if (originalObj.hasOwnProperty(key)) {
      if (JSON.stringify(originalObj[key]) !== JSON.stringify(updatedObj[key])) {
        changedKeys.push(key);
      }
    }
  }

  return changedKeys;
}